import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  MapPin, 
  CheckCircle, 
  XCircle, 
  Clock,
  Search,
  Filter,
  X,
  Save
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { Base_Url } from '../config/domainConfig';
import './CityMaster.css';

const API_BASE_URL = `${Base_Url}/api`;

const CityMaster = () => {
  const { showSuccess, showError } = useToast();
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    cityname: '',
    stateid: '',
    countryid: '',
    status: 'active'
  });
  
  const [formErrors, setFormErrors] = useState({});

  // Fetch cities data
  const fetchCities = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/city-master`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCities(Array.isArray(result.data) ? result.data : []);
        } else {
          showError('Failed to fetch cities: ' + result.message);
          setCities([]);
        }
      } else {
        showError('Failed to fetch cities');
        setCities([]);
      }
    } catch (error) {
      showError('Error fetching cities: ' + error.message);
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch states for dropdown
  const fetchStates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/state-master`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setStates(Array.isArray(result.data) ? result.data : []);
        } else {
          console.error('Failed to fetch states:', result.message);
          setStates([]);
        }
      } else {
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    }
  };

  // Fetch countries for dropdown
  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/country-master/active/list`);
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          setCountries(Array.isArray(result.data) ? result.data : []);
        } else {
          console.error('Failed to fetch countries:', result.message);
          setCountries([]);
        }
      } else {
        setCountries([]);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries([]);
    }
  };

  useEffect(() => {
    fetchCities();
    fetchStates();
    fetchCountries();
  }, []);

  // Filter cities based on search and filters
  const filteredCities = Array.isArray(cities) ? cities.filter(city => {
    const matchesSearch = city.cityname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         city.state?.statename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         city.country?.countryname?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || city.status === statusFilter;
    const matchesState = stateFilter === 'all' || city.stateid === parseInt(stateFilter);
    const matchesCountry = countryFilter === 'all' || city.countryid === parseInt(countryFilter);
    
    return matchesSearch && matchesStatus && matchesState && matchesCountry;
  }) : [];

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.cityname.trim()) {
      errors.cityname = 'City name is required';
    }
    
    if (!formData.stateid) {
      errors.stateid = 'State is required';
    }
    
    if (!formData.countryid) {
      errors.countryid = 'Country is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle add city
  const handleAddCity = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/city-master`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        showSuccess('City added successfully!');
        setShowAddModal(false);
        resetForm();
        fetchCities();
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Failed to add city');
      }
    } catch (error) {
      showError('Error adding city: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit city
  const handleEditCity = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/city-master/${selectedCity.cityid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        showSuccess('City updated successfully!');
        setShowEditModal(false);
        resetForm();
        fetchCities();
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Failed to update city');
      }
    } catch (error) {
      showError('Error updating city: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete city
  const handleDeleteCity = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/city-master/${selectedCity.cityid}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        showSuccess('City deleted successfully!');
        setShowDeleteModal(false);
        fetchCities();
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Failed to delete city');
      }
    } catch (error) {
      showError('Error deleting city: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Open edit modal
  const openEditModal = (city) => {
    setSelectedCity(city);
    setFormData({
      cityname: city.cityname,
      stateid: city.stateid,
      countryid: city.countryid,
      status: city.status
    });
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (city) => {
    setSelectedCity(city);
    setShowDeleteModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      cityname: '',
      stateid: '',
      countryid: '',
      status: 'active'
    });
    setFormErrors({});
    setSelectedCity(null);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'success',
      inactive: 'danger'
    };
    
    return (
      <span className={`status-badge ${statusClasses[status] || 'default'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="loading">
        <div>Loading cities...</div>
      </div>
    );
  }

  return (
    <div className="city-master">
      <div className="city-header">
        <h2>City Master</h2>
        <p>Manage city information and locations</p>
      </div>

      {/* Search and Filters */}
      <div className="controls-section">
        <div className="search-filter">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search cities, states, or countries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filters">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All States</option>
              {Array.isArray(states) && states.map(state => (
                <option key={state.stateid} value={state.stateid}>
                  {state.statename}
                </option>
              ))}
            </select>
            
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Countries</option>
              {Array.isArray(countries) && countries.map(country => (
                <option key={country.countryid} value={country.countryid}>
                  {country.countryname}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <button
          className="btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          <Plus size={20} />
          Add City
        </button>
      </div>

      {/* Cities Table */}
      <div className="table-container">
        <table className="city-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>City Name</th>
              <th>State</th>
              <th>Country</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCities.map(city => (
              <tr key={city.cityid}>
                <td>{city.cityid}</td>
                <td>
                  <div className="city-info">
                    <MapPin size={16} />
                    {city.cityname}
                  </div>
                </td>
                <td>
                  <div className="state-info">
                    {city.state?.statename || 'N/A'}
                    <span className="state-code">({city.state?.statecode || 'N/A'})</span>
                  </div>
                </td>
                <td>
                  <div className="country-info">
                    {city.country?.countryname || 'N/A'}
                    <span className="country-code">({city.country?.countrycode || 'N/A'})</span>
                  </div>
                </td>
                <td>{getStatusBadge(city.status)}</td>
                <td>{new Date(city.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon"
                      onClick={() => openEditModal(city)}
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn-icon danger"
                      onClick={() => openDeleteModal(city)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredCities.length === 0 && (
          <div className="no-data">
            <p>No cities found</p>
          </div>
        )}
      </div>

      {/* Add City Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New City</h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAddCity}>
              <div className="form-group">
                <label>City Name *</label>
                <input
                  type="text"
                  name="cityname"
                  value={formData.cityname}
                  onChange={handleInputChange}
                  className={formErrors.cityname ? 'error' : ''}
                  placeholder="Enter city name"
                />
                {formErrors.cityname && <span className="error-text">{formErrors.cityname}</span>}
              </div>
              
              <div className="form-group">
                <label>Country *</label>
                <select
                  name="countryid"
                  value={formData.countryid}
                  onChange={handleInputChange}
                  className={formErrors.countryid ? 'error' : ''}
                >
                  <option value="">Select Country</option>
                  {Array.isArray(countries) && countries.map(country => (
                    <option key={country.countryid} value={country.countryid}>
                      {country.countryname} ({country.countrycode})
                    </option>
                  ))}
                </select>
                {formErrors.countryid && <span className="error-text">{formErrors.countryid}</span>}
              </div>
              
              <div className="form-group">
                <label>State *</label>
                <select
                  name="stateid"
                  value={formData.stateid}
                  onChange={handleInputChange}
                  className={formErrors.stateid ? 'error' : ''}
                  disabled={!formData.countryid}
                >
                  <option value="">Select State</option>
                  {Array.isArray(states) && states
                    .filter(state => !formData.countryid || state.countryid === parseInt(formData.countryid))
                    .map(state => (
                      <option key={state.stateid} value={state.stateid}>
                        {state.statename} ({state.statecode})
                      </option>
                    ))}
                </select>
                {formErrors.stateid && <span className="error-text">{formErrors.stateid}</span>}
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Adding...' : 'Add City'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit City Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit City</h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleEditCity}>
              <div className="form-group">
                <label>City Name *</label>
                <input
                  type="text"
                  name="cityname"
                  value={formData.cityname}
                  onChange={handleInputChange}
                  className={formErrors.cityname ? 'error' : ''}
                  placeholder="Enter city name"
                />
                {formErrors.cityname && <span className="error-text">{formErrors.cityname}</span>}
              </div>
              
              <div className="form-group">
                <label>Country *</label>
                <select
                  name="countryid"
                  value={formData.countryid}
                  onChange={handleInputChange}
                  className={formErrors.countryid ? 'error' : ''}
                >
                  <option value="">Select Country</option>
                  {Array.isArray(countries) && countries.map(country => (
                    <option key={country.countryid} value={country.countryid}>
                      {country.countryname} ({country.countrycode})
                    </option>
                  ))}
                </select>
                {formErrors.countryid && <span className="error-text">{formErrors.countryid}</span>}
              </div>
              
              <div className="form-group">
                <label>State *</label>
                <select
                  name="stateid"
                  value={formData.stateid}
                  onChange={handleInputChange}
                  className={formErrors.stateid ? 'error' : ''}
                  disabled={!formData.countryid}
                >
                  <option value="">Select State</option>
                  {Array.isArray(states) && states
                    .filter(state => !formData.countryid || state.countryid === parseInt(formData.countryid))
                    .map(state => (
                      <option key={state.stateid} value={state.stateid}>
                        {state.statename} ({state.statecode})
                      </option>
                    ))}
                </select>
                {formErrors.stateid && <span className="error-text">{formErrors.stateid}</span>}
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update City'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Delete City</h3>
              <button
                className="btn-icon"
                onClick={() => setShowDeleteModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to delete the city <strong>"{selectedCity?.cityname}"</strong>?</p>
              <p>This action cannot be undone.</p>
            </div>
            
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDeleteCity}
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete City'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CityMaster; 